import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputLabel from '@mui/material/InputLabel';
import MuiTextField, { TextFieldProps } from '@mui/material/TextField';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { FC, Fragment, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  PolicyRepresentationDto,
  PolicyUpdateDto,
} from '@bom-nextgen-keycloak/models';
import {
  DECISION_STRATEGIES,
  POLICY_LOGICS,
  POLICY_TYPES,
  selectPermissionPolicyDetail,
  useAlertMessage,
} from '@bom-nextgen-keycloak/web/core';
import {
  ErrorMessage,
  fetchClientById,
  fetchClients,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  AutoCompleteOption,
  Capitalize,
  DropdownFieldPro,
  FormActionsGroup,
  FormBox,
  FormField,
  FormText,
  sortAutocompleteOption,
  TextField,
  TableForm,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  getButtonStatus,
  getFieldError,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';
import { CONFIG } from '../../shared';
import { ClientForm } from './config';

const FORM_FIELDS = { ...CONFIG.form.fields };

type GeneralClientType = {
  data: PolicyRepresentationDto | null;
  isUpdating: boolean;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (payload: PolicyUpdateDto) => void;
};

const GeneralClient: FC<GeneralClientType> = ({
  data,
  isUpdating,
  loading,
  onCancel,
  onSubmit,
}) => {
  const params = useParams();
  const { type = 'undefined' } = params;
  const { isEditGeneralTab: canEdit } = useSelector(
    selectPermissionPolicyDetail
  );
  const { setAlertMessage } = useAlertMessage();
  const [selectedClients, setSelectedClients] = useState<AutoCompleteOption[]>(
    []
  );
  const [lookupClients, setLookupClients] = useState<AutoCompleteOption[]>([]);
  const [currentClientList, setCurrentClientList] = useState<
    AutoCompleteOption[]
  >([]);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [autocompletePlaceholder, setAutocompletePlaceholder] = useState(
    FORM_FIELDS.clientId.placeholder
  );
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    CONFIG.ui.debounceDelay
  );

  const disableField = !canEdit;
  const clients = data?.clients || [];
  const hasClients = Boolean(clients.length);
  const hasCurrentSearchTerm = currentSearchTerm !== '';
  const hasCurrentClientList = currentClientList.length;
  const hasClientList = hasCurrentSearchTerm && hasCurrentClientList;
  const queryKeys = {
    clientDetail: [QUERY_KEY.CLIENT_DETAIL, 'POLICY_GENERAL_CBAC', clients],
    clientList: [
      QUERY_KEY.CLIENT_LIST,
      'POLICY_GENERAL_CBAC',
      debouncedSearchQuery,
    ],
  };

  const clientDetailQuery = useQuery(
    queryKeys.clientDetail,
    async () => {
      return Promise.all(
        clients.map(async (item) => {
          const client = await fetchClientById(item);

          const payload: AutoCompleteOption = {
            label: client?.name ?? 'undefined',
            value: {
              id: client?.id ?? 'undefined',
              name: client?.name ?? 'undefined',
            },
          };

          return payload;
        })
      );
    },
    {
      enabled: hasClients,
      onError: (error: AxiosError<ErrorMessage>) => {
        const message =
          error.response?.data.message || 'Cannot get client detail';

        setAlertMessage({ message, typeStatusMessage: 'error' });
      },
      staleTime: CONFIG.query.staleTime,
    }
  );

  const clientListQuery = useQuery(
    queryKeys.clientList,
    async () => {
      const clients = await fetchClients({
        ...CONFIG.pagination,
        clientId: debouncedSearchQuery,
      });

      const payload = clients.map((item) => ({
        label: item?.name ?? 'undefined',
        value: {
          id: item?.id ?? 'undefined',
        },
      }));

      return payload;
    },
    {
      enabled: debouncedSearchQuery !== '',
      onError: (error: AxiosError<ErrorMessage>) => {
        const message = error.response?.data.message || 'Cannot get clients';

        setAlertMessage({
          message,
          statusCode: error.response?.status,
          typeStatusMessage: 'error',
        });
      },
      onSettled: () => {
        setCurrentSearchTerm(searchQuery);
      },
      onSuccess: (data) => {
        if (data.length) {
          const selectedIds: string[] = selectedClients.map(
            (item) => item.value.id || 'undefinded'
          );

          const filteredClients = data.filter((item) => {
            return !selectedIds.includes(item.value.id);
          });

          const mergedClients: AutoCompleteOption[] = [
            ...filteredClients,
            ...selectedClients,
          ];

          setCurrentClientList(sortAutocompleteOption(filteredClients));
          setLookupClients(sortAutocompleteOption(mergedClients));
        } else {
          setCurrentClientList([]);
          setLookupClients(sortAutocompleteOption(selectedClients));
        }
      },
      staleTime: CONFIG.query.staleTime,
    }
  );

  const resetAutocompletePlaceholder = () => {
    setAutocompletePlaceholder(FORM_FIELDS.clientId.placeholder);
  };

  const resetAutocompleteQuery = () => {
    setCurrentSearchTerm('');
    resetAutocompletePlaceholder();
  };

  const handleAutocompleteBlur = () => {
    const prefix = FORM_FIELDS.clientId.placeholder;
    const keyword = currentSearchTerm;

    if (hasCurrentSearchTerm && !hasCurrentClientList) {
      resetAutocompleteQuery();
    }

    if (hasClientList) {
      setAutocompletePlaceholder(
        `${prefix} or click to view options for "${keyword}"`
      );
    }
  };

  const handleAutocompleteClose = () => {
    if (hasClientList) {
      resetAutocompletePlaceholder();
    }
  };

  const handleAutocompleteOpen = () => {
    const prefix = FORM_FIELDS.clientId.placeholder;
    const keyword = currentSearchTerm;

    if (hasClientList) {
      setAutocompletePlaceholder(
        `${prefix} or select from the available options for "${keyword}"`
      );
    }
  };

  const handleAutocompleteOptionRemove = (
    callback: (field: string, value: AutoCompleteOption[]) => void,
    fieldName: string,
    optionItem: AutoCompleteOption,
    optionList: AutoCompleteOption[]
  ) => {
    if (optionItem) {
      const payload = optionList.filter(
        (item) => item.value.id !== optionItem.value.id
      );

      callback(fieldName, payload);
      setSelectedClients(payload);
    }
  };

  const handleAutocompleteOptionChange = (value: AutoCompleteOption[]) => {
    setSelectedClients(value);
  };

  const handleAutocompleteInputChange = (
    event: React.SyntheticEvent,
    value: string
  ) => {
    setSearchQuery(value);
  };

  const handleSubmit = (value: PolicyUpdateDto) => {
    resetAutocompleteQuery();
    onSubmit(value);
  };

  const renderAutocompleteSpinner = (isLoading: boolean) => {
    return isLoading ? <CircularProgress color="inherit" size={16} /> : null;
  };

  useEffect(() => {
    if (clientDetailQuery.isSuccess && clientDetailQuery.data) {
      const clients = sortAutocompleteOption(clientDetailQuery.data);

      setSelectedClients(clients);
      setLookupClients(clients);
    }
  }, [clientDetailQuery.isSuccess, clientDetailQuery.data]);

  const initialValues: ClientForm = {
    clientId: sortAutocompleteOption(clientDetailQuery.data),
    description: data?.description || CONFIG.form.fields.description.value,
    logic: data?.logic || CONFIG.form.fields.logic.value,
    name: data?.name || CONFIG.form.fields.name.value,
    type: POLICY_TYPES.client,
  };

  const clientOptions = lookupClients || [];
  const isClientOptionsLoading =
    clientDetailQuery.isLoading || clientListQuery.isLoading;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(
        payload: ClientForm,
        { setSubmitting }: FormikHelpers<ClientForm>
      ) => {
        const requestPayload: PolicyUpdateDto = {
          decisionStrategy: DECISION_STRATEGIES.unanimous.value,
          clients: payload.clientId.map(
            (item) => item.value?.id || 'undefined'
          ),
          description: payload.description,
          id: data?.id || 'undefined',
          logic: payload.logic,
          name: payload.name,
          type: payload.type,
        };

        handleSubmit(requestPayload);
        setSubmitting(false);
      }}
    >
      {({
        dirty,
        getFieldMeta,
        isValid,
        resetForm,
        setFieldTouched,
        setFieldValue,
        values,
      }) => {
        const { disabled: buttonDisabled } = getButtonStatus({
          canEdit,
          isDirty: dirty,
          isLoading: loading,
          isValid,
        });
        const { clientId } = values;
        const showSelectedList = clientId.length !== 0;
        const fieldProps = { disabled: disableField };
        const autocompleteInputProps: TextFieldProps = {
          fullWidth: true,
          InputLabelProps: {
            shrink: true,
          },
          variant: 'standard',
        };
        const fieldErrors = {
          user: getFieldError(getFieldMeta, FORM_FIELDS.roles.name),
        };

        return (
          <Form>
            <FormBox>
              <FormField>
                <InputLabel shrink>{FORM_FIELDS.type.label}</InputLabel>
                <FormText>
                  <Capitalize>{type}</Capitalize>-based
                </FormText>
              </FormField>
              <FormField>
                <TextField
                  {...fieldProps}
                  label={FORM_FIELDS.name.label}
                  name={FORM_FIELDS.name.name}
                  required
                />
              </FormField>
              <FormField>
                <TextField
                  {...fieldProps}
                  label={FORM_FIELDS.description.label}
                  name={FORM_FIELDS.description.name}
                  required
                />
              </FormField>
              <FormField>
                <Autocomplete
                  {...fieldProps}
                  ChipProps={{ size: 'small' }}
                  clearOnBlur
                  disableClearable
                  filterSelectedOptions
                  getOptionLabel={(option) => option.label}
                  id={FORM_FIELDS.clientId.name}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  limitTags={3}
                  loading={isClientOptionsLoading}
                  multiple
                  onBlur={handleAutocompleteBlur}
                  onChange={(event, value) => {
                    setFieldValue(FORM_FIELDS.clientId.name, value || []);
                    handleAutocompleteOptionChange(value);
                  }}
                  onClose={handleAutocompleteClose}
                  onInputChange={handleAutocompleteInputChange}
                  onOpen={handleAutocompleteOpen}
                  options={clientOptions}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      {...fieldProps}
                      {...autocompleteInputProps}
                      error={fieldErrors.user.isError}
                      helperText={fieldErrors.user.helperText}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {renderAutocompleteSpinner(isClientOptionsLoading)}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                      label={FORM_FIELDS.clientId.label}
                      onBlur={() => {
                        setFieldTouched(FORM_FIELDS.clientId.name);
                      }}
                      placeholder={
                        isClientOptionsLoading
                          ? 'Loading...'
                          : autocompletePlaceholder
                      }
                      required={clientId.length === 0}
                    />
                  )}
                  value={clientId}
                />
              </FormField>

              {showSelectedList && (
                <FormField>
                  <TableForm
                    columnName="Client name"
                    data={clientId}
                    disabled={disableField}
                    onRemove={(item: AutoCompleteOption) =>
                      handleAutocompleteOptionRemove(
                        setFieldValue,
                        FORM_FIELDS.clientId.name,
                        item,
                        clientId
                      )
                    }
                    tableName="client"
                    tooltipTitle="Remove client"
                    unit={`client${clientId.length > 1 ? 's' : ''}`}
                  />
                </FormField>
              )}

              <FormField>
                <DropdownFieldPro
                  {...fieldProps}
                  label={FORM_FIELDS.logic.label}
                  name={FORM_FIELDS.logic.name}
                  options={POLICY_LOGICS}
                  placeholder={FORM_FIELDS.logic.placeholder}
                  required
                />
              </FormField>
            </FormBox>
            <FormActionsGroup
              disableCancel={buttonDisabled.cancel}
              disableReset={buttonDisabled.reset}
              disableSave={buttonDisabled.save}
              loading={isUpdating}
              onCancel={onCancel}
              onReset={resetForm}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export { GeneralClient };
